import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { getVendors } from '../../api';

const LAST_VENDOR_KEY = 'driftops_last_vendor';

/**
 * Vendor selector with dropdown of existing vendors and auto-fill
 */
const VendorSelect = ({ value, onChange, autoFillLast = true }) => {
    const [vendors, setVendors] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Fetch existing vendors on mount
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const vendorList = await getVendors();
                setVendors(vendorList);
            } catch (err) {
                console.error('Failed to fetch vendors:', err);
            }
        };
        fetchVendors();
    }, []);

    // Auto-fill with last used vendor on mount (if enabled and no value set)
    useEffect(() => {
        if (autoFillLast && !value) {
            const lastVendor = localStorage.getItem(LAST_VENDOR_KEY);
            if (lastVendor) {
                setInputValue(lastVendor);
                onChange(lastVendor);
            }
        }
    }, [autoFillLast]);

    // Sync input value with prop
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange(newValue);
        setIsOpen(true);
    };

    const handleSelect = (vendor) => {
        setInputValue(vendor);
        onChange(vendor);
        localStorage.setItem(LAST_VENDOR_KEY, vendor);
        setIsOpen(false);
    };

    const handleBlur = () => {
        // Save to localStorage when user finishes typing
        if (inputValue.trim()) {
            localStorage.setItem(LAST_VENDOR_KEY, inputValue.trim());
        }
    };

    // Filter vendors based on input
    const filteredVendors = vendors.filter(v =>
        v.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Show dropdown if there are matching vendors or if input is empty and vendors exist
    const showDropdown = isOpen && (filteredVendors.length > 0 || (inputValue === '' && vendors.length > 0));

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    onBlur={handleBlur}
                    className="w-full p-2 pr-8 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                    type="button"
                    onClick={() => {
                        setIsOpen(!isOpen);
                        inputRef.current?.focus();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                    <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {showDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-[#252526] border border-[#3c3c3c] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredVendors.length === 0 && inputValue === '' && vendors.length > 0 ? (
                        vendors.map((vendor) => (
                            <button
                                key={vendor}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelect(vendor)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-[#3c3c3c] flex items-center gap-2 text-gray-300"
                            >
                                <Building2 size={14} className="text-gray-400" />
                                {vendor}
                            </button>
                        ))
                    ) : (
                        filteredVendors.map((vendor) => (
                            <button
                                key={vendor}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelect(vendor)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-[#3c3c3c] flex items-center gap-2 text-gray-300"
                            >
                                <Building2 size={14} className="text-gray-400" />
                                {vendor}
                            </button>
                        ))
                    )}
                    {inputValue && !vendors.includes(inputValue) && (
                        <div className="px-3 py-2 text-xs text-gray-400 border-t border-[#3c3c3c]">
                            Press Enter to create "{inputValue}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VendorSelect;
